import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "@/config/firebase";
import { getOwnedIslands, FlyingIsland } from "@/config/flyingIslands";
import { InventorySingleton } from "../../scripts/inventory";
import { RarityInventory } from "@/types"
import { getTelegramId } from "@/utils/telegram";


interface UserVoxelInventory {
  available: RarityInventory,
  used: RarityInventory,
  staked: RarityInventory
}
export interface User {
  uid: string;
  username: string;
  walletId: string | null;
  points?: number;
  bananium?: number;
  token404?: number;
  lastBananiumClaimTimestamp?: number;
  virtualWalletId?: string | null;
  telegramId?: string | null;
  isAdmin?: boolean;
  referralCount?: number;
  referralRewardsEarned?: number;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean;
  loadingInventory: boolean;
  islands: ({ id: string } & FlyingIsland)[];
  // Use the new type for voxelInventory
  voxelInventory: UserVoxelInventory | null;
  updateIslands: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);


const SESSION_DURATION = 1000 * 60 * 60; // 1 hour
const ACTIVITY_TIMEOUT = 1000 * 60 * 30; // 30 minutes
const SESSION_CHECK_INTERVAL = 1000 * 60; // 1 minute


export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(true);
  const [islands, setIslands] = useState<({ id: string } & FlyingIsland)[]>([]);
  // Use the new type for the voxelInventory state
  const [voxelInventory, setVoxelInventory] = useState<UserVoxelInventory | null>(null);

  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('lastActiveUser');
    try {
      // Basic parsing, rely on Firestore listener for full data
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error("Failed to parse user from localStorage:", e);
      return null;
    }
  });

  // Handle user login/logout and initial island fetch
  useEffect(() => {
    if (user) {
      localStorage.setItem('lastActiveUser', JSON.stringify({
        uid: user.uid,
        username: user.username,
      }));
      localStorage.setItem('sessionStart', Date.now().toString());
      getOwnedIslands(user.uid).then(setIslands).catch(console.error);
    } else {
      localStorage.removeItem('lastActiveUser');
      localStorage.removeItem('sessionStart');
      setIslands([]);
      setVoxelInventory(null);
    }
  }, [user]);

  // Session timeout check
  useEffect(() => {
    if (user) {
      const checkSession = () => {
        const sessionStart = localStorage.getItem('sessionStart');
        if (sessionStart && Date.now() - Number(sessionStart) > SESSION_DURATION) {
          setUser(null);
        }
      };

      const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [user]);

  // User activity monitoring
  useEffect(() => {
    let activityTimer: NodeJS.Timeout;

    const resetActivityTimer = () => {
      if (activityTimer) clearTimeout(activityTimer);
      if (user) {
        activityTimer = setTimeout(() => {
          // console.log("User inactive, logging out.");
          setUser(null);
        }, ACTIVITY_TIMEOUT);
      }
    };

    const activities = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      resetActivityTimer();
    };

    if (user) {
      activities.forEach(event => {
        window.addEventListener(event, handleActivity);
      });
      resetActivityTimer();
    }

    return () => {
      activities.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (activityTimer) clearTimeout(activityTimer);
    };
  }, [user]);

  // Listener for main user document
  useEffect(() => {
    let unsubscribe: () => void = () => { };

    if (user?.uid) {
      // console.log(`Setting up user document listener for ${user.uid}`);
      const userDocRef = doc(db, 'users', user.uid);

      unsubscribe = onSnapshot(userDocRef, (snapshot) => {
        if (snapshot.exists()) {
          const userDataFromFirestore = snapshot.data() as User; // Cast to User type

          setUser(prevUser => {
            // Perform checks to avoid unnecessary state updates
            const isUserDataChanged = prevUser && (
              prevUser.username !== userDataFromFirestore.username ||
              prevUser.points !== userDataFromFirestore.points ||
              prevUser.bananium !== userDataFromFirestore.bananium ||
              prevUser.token404 !== userDataFromFirestore.token404 ||
              prevUser.walletId !== userDataFromFirestore.walletId ||
              prevUser.virtualWalletId !== userDataFromFirestore.virtualWalletId ||
              prevUser.telegramId !== userDataFromFirestore.telegramId ||
              prevUser.referralCount !== userDataFromFirestore.referralCount ||
              prevUser.referralRewardsEarned !== userDataFromFirestore.referralRewardsEarned
              // Add checks for other fields you listen to
            );


            if (isUserDataChanged) {
              // console.log(`User data changed for ${user.uid}, updating context state.`);
              // Return a NEW object with updated fields
              return {
                ...prevUser,
                username: userDataFromFirestore.username,
                points: userDataFromFirestore.points,
                bananium: userDataFromFirestore.bananium,
                token404: userDataFromFirestore.token404,
                walletId: userDataFromFirestore.walletId,
                virtualWalletId: userDataFromFirestore.virtualWalletId,
                telegramId: userDataFromFirestore.telegramId,
                isAdmin: userDataFromFirestore.isAdmin,
                referralCount: userDataFromFirestore.referralCount,
                referralRewardsEarned: userDataFromFirestore.referralRewardsEarned
                // Update other fields here
              };
            } else if (!prevUser) {
              // Handle initial load case
              //  console.log("Listener got initial user data, setting user state.");
              return userDataFromFirestore;
            }
            else {
              // Data is the same, return previous state
              return prevUser;
            }
          });

        } else {
          console.warn(`User document not found for UID: ${user.uid} during snapshot. Logging out.`);
          setUser(null); // Log out if the document is deleted
        }
        setLoading(false);
      }, (error) => {
        console.error("Error listening to user document:", error);
        // Handle listener errors, potentially log out user or show error
        setUser(null); // Log out on error
        setLoading(false);
      });

      return () => {
        // console.log(`Unsubscribing from user document listener for ${user.uid}`);
        unsubscribe(); // Cleanup the listener
      };

    } else {
      // Cleanup function for when no user is logged in
      setLoading(false);
      return () => { console.log("No user, no user document listener active."); };
    }
  }, [user?.uid]); // Depend on user.uid to re-subscribe


  // Listener for voxel inventory document
  useEffect(() => {
    let unsubscribe: () => void = () => { };

    if (user?.uid) {
      // console.log(`Setting up voxel inventory listener for ${user.uid}`);
      setLoadingInventory(true);
      const docRef = doc(db, 'userVoxels', user.uid);

      unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Ensure the data structure matches UserVoxelInventory
          if (data && typeof data.voxelInventory === 'object' && data.voxelInventory !== null &&
            typeof data.voxelInventory.available === 'object' && data.voxelInventory.available !== null &&
            typeof data.voxelInventory.used === 'object' && data.voxelInventory.used !== null &&
            typeof data.voxelInventory.staked === 'object' && data.voxelInventory.staked !== null) {

            const fetchedInventory = data.voxelInventory as UserVoxelInventory;
            setVoxelInventory(fetchedInventory);

            if (InventorySingleton && (InventorySingleton as any).updateInventory) {
              (InventorySingleton as any).updateInventory(fetchedInventory);
            } else {
              console.error("InventorySingleton is missing or updateInventory method is not available!");
            }

          } else {
            console.warn('User voxel document found, but voxelInventory data is missing or malformed.');
            setVoxelInventory(null);
          }
        } else {
          // console.log('No voxel data found for user, setting inventory to null.');
          setVoxelInventory(null);
        }
        setLoadingInventory(false);
      }, (error) => {
        console.error("Error listening to voxel inventory:", error);
        setVoxelInventory(null);
        setLoadingInventory(false);
      });

      return () => {
        // console.log(`Unsubscribing from voxel inventory listener for ${user.uid}`);
        unsubscribe();
      };

    } else {
      setVoxelInventory(null);
      setLoadingInventory(false);
      return () => { console.log("No user, no voxel inventory listener active."); };
    }
  }, [user?.uid]);


  // Sync Telegram ID if missing on user profile but found locally (e.g. restored from CloudStorage)
  useEffect(() => {
    if (user?.uid && !user.telegramId) {
      const checkAndSync = () => {
        const localTgId = getTelegramId();
        if (localTgId) {
          console.log(`[UserContext] Syncing missing Telegram ID: ${localTgId} to user ${user.uid}`);
          const userRef = doc(db, 'users', user.uid);
          updateDoc(userRef, { telegramId: localTgId })
            .catch(err => console.error("[UserContext] Failed to sync Telegram ID:", err));
        }
      };

      // Valid to check immediately
      checkAndSync();

      // Also check after a small delay in case CloudStorage restore is slightly async
      const timer = setTimeout(checkAndSync, 2000);
      return () => clearTimeout(timer);
    }
  }, [user?.uid, user?.telegramId]);


  // Function to refetch islands (used after purchase)
  const updateIslands = () => {
    if (user?.uid)
      getOwnedIslands(user.uid).then(setIslands).catch(console.error);
  }


  return (
    <UserContext.Provider value={{
      user,
      setUser,
      loading,
      loadingInventory,
      islands,
      voxelInventory,
      updateIslands,
      setLoading
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
